import { boolean, createBuilder, createSchema, defineMutatorWithType, defineMutators, defineQueries, defineQueryWithType, enumeration, json, number, relationships, string, table, } from "@rocicorp/zero";
import { validateEquipmentTagParent } from "./equipment-tag-depth.js";
import { z } from "zod";
const personTypes = ["contact", "employee", "freelancer"];
const projectEquipmentGroups = table("projectEquipmentGroups")
    .from("project_equipment_groups")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    packingState: enumeration().from("packing_state"),
    riggingState: enumeration().from("rigging_state"),
    displayOrder: number().from("display_order").optional(),
    isCollapsed: boolean().from("is_collapsed").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectEquipmentAssignments = table("projectEquipmentAssignments")
    .from("project_equipment_assignments")
    .columns({
    id: string(),
    groupID: string().from("group_id"),
    equipmentID: string().from("equipment_id").optional(),
    temporaryEquipmentID: string().from("temporary_equipment_id").optional(),
    amount: number(),
    notes: string().optional(),
    cameraPlanColumnKey: string().from("camera_plan_column_key").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectEquipmentInstanceAssignments = table("projectEquipmentInstanceAssignments")
    .from("project_equipment_instance_assignments")
    .columns({
    id: string(),
    assignmentID: string().from("assignment_id"),
    instanceID: string().from("instance_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const temporaryEquipment = table("temporaryEquipment")
    .from("temporary_equipment")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    countryOfOrigin: string().from("country_of_origin").optional(),
    carnetAtaDescription: string().from("carnet_ata_description").optional(),
    carnetAtaValue: number().from("carnet_ata_value").optional(),
    weight: number().optional(),
    carnetAtaWeightUnit: string().from("carnet_ata_weight_unit").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const equipment = table("equipment")
    .columns({
    id: string(),
    number: string().optional(),
    name: string(),
    manufacturer: string().optional(),
    icon: string().optional(),
    newPrice: number().from("new_price").optional(),
    rentalPrice: number().from("rental_price").optional(),
    isSerialized: boolean().from("is_serialized"),
    stockQuantity: number().from("stock_quantity").optional(),
    notes: string().optional(),
    parentEquipmentID: string().from("parent_equipment_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
    rentmanID: number().from("rentman_id").optional(),
    weight: number().optional(),
    width: number().optional(),
    height: number().optional(),
    length: number().optional(),
    volume: number().optional(),
    power: number().optional(),
    currentDraw: number().from("current_draw").optional(),
    qrCode: string().from("qr_code").optional(),
    externalRemark: string().from("external_remark").optional(),
    locationInWarehouse: string().from("location_in_warehouse").optional(),
    shortName: string().from("short_name").optional(),
    countryOfOrigin: string().from("country_of_origin").optional(),
    customsTariffNumber: string().from("customs_tariff_number").optional(),
    carnetAtaDescription: string().from("carnet_ata_description").optional(),
    carnetAtaValue: number().from("carnet_ata_value").optional(),
    carnetAtaWeightUnit: string().from("carnet_ata_weight_unit").optional(),
})
    .primaryKey("id");
const equipmentInstances = table("equipmentInstances")
    .from("equipment_instances")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    identifier: string(),
    notes: string().optional(),
    acquisitionDate: number().from("acquisition_date").optional(),
    acquisitionPrice: number().from("acquisition_price").optional(),
    vendorID: string().from("vendor_id").optional(),
    status: enumeration(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    qrCode: string().from("qr_code").optional(),
    serialNumber: string().from("serial_number").optional(),
})
    .primaryKey("id");
const equipmentTagAssignments = table("equipmentTagAssignments")
    .from("equipment_tag_assignments")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    tagID: string().from("tag_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const equipmentTags = table("equipmentTags")
    .from("equipment_tags")
    .columns({
    id: string(),
    name: string(),
    parentID: string().from("parent_id").optional(),
    icon: string().optional(),
    rentmanID: number().from("rentman_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const equipmentImages = table("equipmentImages")
    .from("equipment_images")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    imageURL: string().from("image_url"),
    displayOrder: number().from("display_order").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const equipmentContents = table("equipmentContents")
    .from("equipment_contents")
    .columns({
    id: string(),
    parentEquipmentID: string().from("parent_equipment_id"),
    // Null for free lines, which only have a name
    childEquipmentID: string().from("child_equipment_id").optional(),
    name: string().optional(),
    quantity: number(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlans = table("cameraPlans")
    .from("camera_plans")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    description: string().optional(),
    mapType: enumeration().from("map_type"),
    imageURL: string().from("image_url").optional(),
    embedURL: string().from("embed_url").optional(),
    mapCenterLng: number().from("map_center_lng").optional(),
    mapCenterLat: number().from("map_center_lat").optional(),
    mapZoom: number().from("map_zoom").optional(),
    eventDate: number().from("event_date").optional(),
    sortOrder: number().from("sort_order").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlanColumns = table("cameraPlanColumns")
    .from("camera_plan_columns")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    name: string(),
    columnType: enumeration().from("column_type"),
    options: json(),
    sortOrder: number().from("sort_order").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const cameraPlanOverlays = table("cameraPlanOverlays")
    .from("camera_plan_overlays")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    overlayType: enumeration().from("overlay_type"),
    positionX: number().from("position_x"),
    positionY: number().from("position_y"),
    width: number(),
    height: number(),
    rotation: number(),
    color: string(),
    opacity: number(),
    strokeWidth: number().from("stroke_width"),
    fill: boolean(),
    imageURL: string().from("image_url").optional(),
    label: string().optional(),
    fontSize: number().from("font_size"),
    points: json().optional(),
    zIndex: number().from("z_index"),
    sortOrder: number().from("sort_order"),
    locked: boolean(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameras = table("cameras")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    number: string(),
    description: string().optional(),
    icon: string().optional(),
    positionX: number().from("position_x").optional(),
    positionY: number().from("position_y").optional(),
    rotation: number().optional(),
    track: json().optional(),
    customValues: json().from("custom_values"),
    sortOrder: number().from("sort_order").optional(),
    color: string().optional(),
    equipmentGroupID: string().from("equipment_group_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraFunctionAssignments = table("cameraFunctionAssignments")
    .from("camera_function_assignments")
    .columns({
    id: string(),
    cameraID: string().from("camera_id"),
    functionAssignmentID: string().from("function_assignment_id"),
})
    .primaryKey("id");
export const functionAssignments = table("functionAssignments")
    .from("function_assignments")
    .columns({
    id: string(),
    functionID: string().from("function_id"),
    personID: string().from("person_id"),
    remark: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const peopleTable = table("people")
    .columns({
    id: string(),
    userID: string().from("user_id").optional(),
    companyID: string().from("company_id").optional(),
    firstName: string().from("first_name"),
    lastName: string().from("last_name"),
    number: string().optional(),
    function: string().optional(),
    phone: string().optional(),
    email: string().optional(),
    personType: enumeration().from("person_type"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
    isAdmin: boolean().from("is_admin"),
    clockifyUserID: string().from("clockify_user_id").optional(),
    rentmanID: number().from("rentman_id").optional(),
    allergies: string().optional(),
    remarks: string().optional(),
    notes: string().optional(),
    allergiesNotes: string().from("allergies_notes").optional(),
    unityUsername: string().from("unity_username").optional(),
})
    .primaryKey("id");
// ---------------------------------------------------------------------------
// SIM cards (read-only in Zero; writes go through /api/sims)
// ---------------------------------------------------------------------------
export const simCards = table("simCards")
    .from("sim_cards")
    .columns({
    id: string(),
    iccid: string(),
    provider: enumeration(),
    source: enumeration(),
    msisdn: string().optional(),
    imsi: string().optional(),
    imei: string().optional(),
    carrierAccountNbr: string().from("carrier_account_nbr").optional(),
    carrierAccountGuid: string().from("carrier_account_guid").optional(),
    status: string().optional(),
    statusLabel: string().from("status_label").optional(),
    ratePlan: string().from("rate_plan").optional(),
    commPlan: string().from("comm_plan").optional(),
    equipmentInstanceID: string().from("equipment_instance_id").optional(),
    slot: number().optional(),
    label: string().optional(),
    /** Internal note, hub only (never sent to Simetric). */
    note: string().optional(),
    simetricSegment1: string().from("simetric_segment1").optional(),
    simetricSegment2: string().from("simetric_segment2").optional(),
    simetricSegment3: string().from("simetric_segment3").optional(),
    usageCycle: number().from("usage_cycle").optional(),
    ctdDataMb: number().from("ctd_data_mb").optional(),
    ctdSms: number().from("ctd_sms").optional(),
    ctdSessions: number().from("ctd_sessions").optional(),
    usageUpdatedAt: number().from("usage_updated_at").optional(),
    simetricUpdatedAt: number().from("simetric_updated_at").optional(),
    lastSyncedAt: number().from("last_synced_at").optional(),
    missingSince: number().from("missing_since").optional(),
    profileID: string().from("profile_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const simUsageMonthly = table("simUsageMonthly")
    .from("sim_usage_monthly")
    .columns({
    simCardID: string().from("sim_card_id"),
    cycleMonth: number().from("cycle_month"),
    dataMb: number().from("data_mb"),
    sms: number(),
    voiceMin: number().from("voice_min"),
    sessions: number(),
    fetchedAt: number().from("fetched_at").optional(),
})
    .primaryKey("simCardID", "cycleMonth");
export const simProfiles = table("simProfiles")
    .from("sim_profiles")
    .columns({
    id: string(),
    provider: enumeration(),
    name: string(),
    isOpen: boolean().from("is_open"),
    ratePlan: string().from("rate_plan").optional(),
    notes: string().optional(),
})
    .primaryKey("id");
export const simProfileNetworks = table("simProfileNetworks")
    .from("sim_profile_networks")
    .columns({
    id: string(),
    profileID: string().from("profile_id"),
    countryCode: string().from("country_code"),
    networks: json(),
    note: string().optional(),
})
    .primaryKey("id");
export const imeiTacs = table("imeiTacs")
    .from("imei_tacs")
    .columns({
    tac: string(),
    brand: string(),
    name: string(),
    specs: string().optional(),
})
    .primaryKey("tac");
export const simUsageDaily = table("simUsageDaily")
    .from("sim_usage_daily")
    .columns({
    simCardID: string().from("sim_card_id"),
    day: number(),
    dataMb: number().from("data_mb"),
    isEstimated: boolean().from("is_estimated"),
    computedAt: number().from("computed_at").optional(),
})
    .primaryKey("simCardID", "day");
export const simActionBatches = table("simActionBatches")
    .from("sim_action_batches")
    .columns({
    id: string(),
    kind: enumeration(),
    requestedBy: string().from("requested_by").optional(),
    equipmentInstanceID: string().from("equipment_instance_id").optional(),
    status: enumeration(),
    summary: json(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const simActionItems = table("simActionItems")
    .from("sim_action_items")
    .columns({
    id: string(),
    batchID: string().from("batch_id"),
    simCardID: string().from("sim_card_id"),
    operation: enumeration(),
    payload: json().optional(),
    state: enumeration(),
    skipReason: string().from("skip_reason").optional(),
    attempts: number(),
    lastError: string().from("last_error").optional(),
    submittedAt: number().from("submitted_at").optional(),
    finishedAt: number().from("finished_at").optional(),
    verifiedAt: number().from("verified_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const groupRelationships = relationships(projectEquipmentGroups, ({ many }) => ({
    assignments: many({
        sourceField: ["id"],
        destSchema: projectEquipmentAssignments,
        destField: ["groupID"],
    }),
}));
const assignmentRelationships = relationships(projectEquipmentAssignments, ({ one, many }) => ({
    group: one({
        sourceField: ["groupID"],
        destSchema: projectEquipmentGroups,
        destField: ["id"],
    }),
    equipment: one({
        sourceField: ["equipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
    temporaryEquipment: one({
        sourceField: ["temporaryEquipmentID"],
        destSchema: temporaryEquipment,
        destField: ["id"],
    }),
    instanceAssignments: many({
        sourceField: ["id"],
        destSchema: projectEquipmentInstanceAssignments,
        destField: ["assignmentID"],
    }),
}));
const instanceAssignmentRelationships = relationships(projectEquipmentInstanceAssignments, ({ one }) => ({
    instance: one({
        sourceField: ["instanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
}));
const equipmentRelationships = relationships(equipment, ({ many }) => ({
    tagAssignments: many({
        sourceField: ["id"],
        destSchema: equipmentTagAssignments,
        destField: ["equipmentID"],
    }),
    images: many({
        sourceField: ["id"],
        destSchema: equipmentImages,
        destField: ["equipmentID"],
    }),
    contents: many({
        sourceField: ["id"],
        destSchema: equipmentContents,
        destField: ["parentEquipmentID"],
    }),
}));
const equipmentContentRelationships = relationships(equipmentContents, ({ one }) => ({
    childEquipment: one({
        sourceField: ["childEquipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
}));
const equipmentTagAssignmentRelationships = relationships(equipmentTagAssignments, ({ one }) => ({
    tag: one({
        sourceField: ["tagID"],
        destSchema: equipmentTags,
        destField: ["id"],
    }),
}));
const equipmentTagRelationships = relationships(equipmentTags, ({ many }) => ({
    assignments: many({
        sourceField: ["id"],
        destSchema: equipmentTagAssignments,
        destField: ["tagID"],
    }),
}));
const cameraPlanRelationships = relationships(cameraPlans, ({ many }) => ({
    columns: many({
        sourceField: ["id"],
        destSchema: cameraPlanColumns,
        destField: ["cameraPlanID"],
    }),
    cameras: many({
        sourceField: ["id"],
        destSchema: cameras,
        destField: ["cameraPlanID"],
    }),
    overlays: many({
        sourceField: ["id"],
        destSchema: cameraPlanOverlays,
        destField: ["cameraPlanID"],
    }),
}));
const cameraRelationships = relationships(cameras, ({ one, many }) => ({
    cameraPlan: one({
        sourceField: ["cameraPlanID"],
        destSchema: cameraPlans,
        destField: ["id"],
    }),
    equipmentGroup: one({
        sourceField: ["equipmentGroupID"],
        destSchema: projectEquipmentGroups,
        destField: ["id"],
    }),
    cameraFunctionAssignments: many({
        sourceField: ["id"],
        destSchema: cameraFunctionAssignments,
        destField: ["cameraID"],
    }),
}));
const cameraFunctionAssignmentRelationships = relationships(cameraFunctionAssignments, ({ one }) => ({
    functionAssignment: one({
        sourceField: ["functionAssignmentID"],
        destSchema: functionAssignments,
        destField: ["id"],
    }),
}));
export const functionAssignmentRelationships = relationships(functionAssignments, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const equipmentInstanceRelationships = relationships(equipmentInstances, ({ one, many }) => ({
    equipment: one({
        sourceField: ["equipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
    simCards: many({
        sourceField: ["id"],
        destSchema: simCards,
        destField: ["equipmentInstanceID"],
    }),
}));
const simCardRelationships = relationships(simCards, ({ one, many }) => ({
    instance: one({
        sourceField: ["equipmentInstanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
    usageMonthly: many({
        sourceField: ["id"],
        destSchema: simUsageMonthly,
        destField: ["simCardID"],
    }),
    usageDaily: many({
        sourceField: ["id"],
        destSchema: simUsageDaily,
        destField: ["simCardID"],
    }),
    actionItems: many({
        sourceField: ["id"],
        destSchema: simActionItems,
        destField: ["simCardID"],
    }),
}));
const simProfileRelationships = relationships(simProfiles, ({ many }) => ({
    networks: many({
        sourceField: ["id"],
        destSchema: simProfileNetworks,
        destField: ["profileID"],
    }),
}));
const simActionBatchRelationships = relationships(simActionBatches, ({ one, many }) => ({
    items: many({
        sourceField: ["id"],
        destSchema: simActionItems,
        destField: ["batchID"],
    }),
    requester: one({
        sourceField: ["requestedBy"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    instance: one({
        sourceField: ["equipmentInstanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
}));
const simActionItemRelationships = relationships(simActionItems, ({ one }) => ({
    simCard: one({
        sourceField: ["simCardID"],
        destSchema: simCards,
        destField: ["id"],
    }),
    batch: one({
        sourceField: ["batchID"],
        destSchema: simActionBatches,
        destField: ["id"],
    }),
}));
export const equipmentPlanTables = [
    projectEquipmentGroups,
    projectEquipmentAssignments,
    projectEquipmentInstanceAssignments,
    temporaryEquipment,
    equipment,
    equipmentInstances,
    equipmentTagAssignments,
    equipmentTags,
    equipmentImages,
    equipmentContents,
    cameraPlans,
    cameraPlanColumns,
    cameraPlanOverlays,
    cameras,
    cameraFunctionAssignments,
    functionAssignments,
    peopleTable,
    simCards,
    simUsageMonthly,
    simUsageDaily,
    simActionBatches,
    simActionItems,
    imeiTacs,
    simProfiles,
    simProfileNetworks,
];
export const equipmentPlanRelationships = [
    groupRelationships,
    assignmentRelationships,
    instanceAssignmentRelationships,
    equipmentRelationships,
    equipmentTagAssignmentRelationships,
    equipmentTagRelationships,
    equipmentContentRelationships,
    cameraPlanRelationships,
    cameraRelationships,
    cameraFunctionAssignmentRelationships,
    functionAssignmentRelationships,
    equipmentInstanceRelationships,
    simCardRelationships,
    simProfileRelationships,
    simActionBatchRelationships,
    simActionItemRelationships,
];
export const equipmentPlanSchema = createSchema({
    tables: equipmentPlanTables,
    relationships: equipmentPlanRelationships,
});
export const equipmentPlanZql = createBuilder(equipmentPlanSchema);
const defineEquipmentQuery = defineQueryWithType();
export const equipmentPlanQueryDefinitions = {
    equipmentTags: {
        list: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentTags.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentTags.orderBy("name", "asc");
        }),
        counts: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentTagAssignments.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentTagAssignments;
        }),
    },
    equipmentCatalog: {
        list: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipment.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipment
                .where("deletedAt", "IS", null)
                .related("tagAssignments", (assignment) => assignment.related("tag"))
                .related("images")
                .orderBy("name", "asc");
        }),
        byID: defineEquipmentQuery(({ args, ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipment
                    .where("id", "__unauthorized__")
                    .one();
            }
            return equipmentPlanZql.equipment
                .where("id", args.id)
                .related("tagAssignments", (assignment) => assignment.related("tag"))
                .related("images")
                .one();
        }),
    },
    equipmentInstances: {
        byEquipment: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .where("equipmentID", args.equipmentID)
                .orderBy("identifier", "asc");
        }),
        byEquipmentIDs: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .where("equipmentID", "IN", args.equipmentIDs)
                .orderBy("identifier", "asc");
        }),
    },
    equipmentContents: {
        byParent: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentContents.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentContents
                .where("parentEquipmentID", args.parentEquipmentID)
                .related("childEquipment")
                .orderBy("createdAt", "asc");
        }),
    },
    equipmentConflicts: {
        assignmentsByEquipment: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.projectEquipmentAssignments.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.projectEquipmentAssignments
                .where("equipmentID", "IN", args.equipmentIDs)
                .related("group")
                .related("instanceAssignments", (instanceAssignment) => instanceAssignment.related("instance"))
                .orderBy("createdAt", "asc");
        }),
    },
    equipmentPlan: {
        byProject: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.projectEquipmentGroups.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.projectEquipmentGroups
                .where("projectID", args.projectID)
                .related("assignments", (assignment) => assignment
                .related("equipment", (eq) => eq.related("tagAssignments"))
                .related("temporaryEquipment")
                .related("instanceAssignments", (instanceAssignment) => instanceAssignment.related("instance")))
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc");
        }),
    },
    cameraPlans: {
        byProject: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlans.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlans
                .where("projectID", args.projectID)
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        withCamerasByProject: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlans.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlans
                .where("projectID", args.projectID)
                .related("cameras", (camera) => camera
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("number", "asc"))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        columnsByPlan: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlanColumns.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlanColumns
                .where("cameraPlanID", args.cameraPlanID)
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        overlaysByPlan: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlanOverlays.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlanOverlays
                .where("cameraPlanID", args.cameraPlanID)
                .orderBy("zIndex", "asc")
                .orderBy("sortOrder", "asc");
        }),
        camerasByPlan: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameras.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameras
                .where("cameraPlanID", args.cameraPlanID)
                .related("equipmentGroup")
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        camerasByProject: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameras.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameras
                .related("cameraPlan", (plan) => plan.where("projectID", args.projectID))
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("number", "asc");
        }),
    },
    sims: {
        list: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("iccid", "asc");
        }),
        byId: defineEquipmentQuery(({ args, ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards
                    .where("id", "__unauthorized__")
                    .one();
            }
            return equipmentPlanZql.simCards
                .where("id", args.id)
                .related("instance", (instance) => instance.related("equipment"))
                .one();
        }),
        byInstance: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .where("equipmentInstanceID", args.instanceID)
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("slot", "asc")
                .orderBy("iccid", "asc");
        }),
        byInstances: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .where("equipmentInstanceID", "IN", args.instanceIDs)
                .orderBy("slot", "asc")
                .orderBy("iccid", "asc");
        }),
        eligibleDevices: defineEquipmentQuery(({ args, ctx, }) => {
            if ((ctx.role !== "admin" && ctx.role !== "employee") ||
                args.tagIDs.length === 0) {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .whereExists("equipment", (eq) => eq
                .where("deletedAt", "IS", null)
                .whereExists("tagAssignments", (assignment) => assignment.where("tagID", "IN", args.tagIDs)))
                .related("equipment")
                .orderBy("identifier", "asc");
        }),
        usageMonthly: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageMonthly.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageMonthly
                .where("cycleMonth", ">=", args.since)
                .orderBy("cycleMonth", "asc");
        }),
        profiles: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simProfiles.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simProfiles
                .related("networks", (network) => network.orderBy("countryCode", "asc"))
                .orderBy("provider", "asc")
                .orderBy("name", "asc");
        }),
        imeiModels: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.imeiTacs.where("tac", "__unauthorized__");
            }
            return equipmentPlanZql.imeiTacs.where("tac", "IN", args.tacs);
        }),
        usageMonthlyBySims: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageMonthly.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageMonthly
                .where("simCardID", "IN", args.simCardIDs)
                .orderBy("cycleMonth", "asc");
        }),
        usageDailyBySims: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageDaily.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageDaily
                .where("simCardID", "IN", args.simCardIDs)
                .where("day", ">=", args.since)
                .orderBy("day", "asc");
        }),
        recentBatches: defineEquipmentQuery(({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simActionBatches.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simActionBatches
                .related("items", (item) => item.related("simCard"))
                .related("requester")
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("createdAt", "desc")
                .limit(Math.min(Math.max(args.limit, 1), 50));
        }),
        openItems: defineEquipmentQuery(({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simActionItems.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simActionItems.where("state", "IN", [
                "queued",
                "submitted",
            ]);
        }),
    },
};
export const equipmentPlanQueries = defineQueries(equipmentPlanQueryDefinitions);
const defineEquipmentMutator = defineMutatorWithType();
const uuidSchema = z.string().uuid();
const trimmedNameSchema = z.string().trim().min(1).max(500);
const nullableUUIDSchema = uuidSchema.nullable().optional();
const nullableStringSchema = z.string().nullable().optional();
const nullableNumberSchema = z.number().nullable().optional();
const assignmentEquipmentRefsShape = {
    equipmentID: nullableUUIDSchema,
    temporaryEquipmentID: nullableUUIDSchema,
};
const equipmentTagIconSchema = z.string().trim().max(64).nullable().optional();
export const createEquipmentTagInputSchema = z.object({
    id: uuidSchema,
    name: trimmedNameSchema,
    parentID: nullableUUIDSchema,
    icon: equipmentTagIconSchema,
});
export const updateEquipmentTagInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    parentID: nullableUUIDSchema,
    icon: equipmentTagIconSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment tag field must be updated");
export const equipmentTagIDInputSchema = z.object({
    id: uuidSchema,
});
const equipmentCatalogInputShape = {
    name: trimmedNameSchema,
    number: nullableStringSchema,
    manufacturer: nullableStringSchema,
    icon: nullableStringSchema,
    newPrice: nullableNumberSchema,
    rentalPrice: nullableNumberSchema,
    isSerialized: z.boolean(),
    stockQuantity: z.number().int().min(0).nullable().optional(),
    notes: nullableStringSchema,
    parentEquipmentID: nullableUUIDSchema,
    rentmanID: z.number().int().nullable().optional(),
    weight: nullableNumberSchema,
    width: nullableNumberSchema,
    height: nullableNumberSchema,
    length: nullableNumberSchema,
    volume: nullableNumberSchema,
    power: nullableNumberSchema,
    currentDraw: nullableNumberSchema,
    qrCode: nullableStringSchema,
    externalRemark: nullableStringSchema,
    locationInWarehouse: nullableStringSchema,
    shortName: nullableStringSchema,
    countryOfOrigin: nullableStringSchema,
    customsTariffNumber: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
};
export const createEquipmentCatalogInputSchema = z.object({
    id: uuidSchema,
    ...equipmentCatalogInputShape,
    tagIDs: z.array(uuidSchema).optional(),
});
export const updateEquipmentCatalogInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(equipmentCatalogInputShape).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
    tagIDs: z.array(uuidSchema).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment field must be updated");
export const softDeleteEquipmentCatalogInputSchema = z.object({
    id: uuidSchema,
});
const equipmentInstanceStatusSchema = z.enum(["active", "repair", "archived"]);
export const createEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
    equipmentID: uuidSchema,
    identifier: trimmedNameSchema,
    notes: nullableStringSchema,
    acquisitionDate: nullableStringSchema,
    acquisitionPrice: nullableNumberSchema,
    vendorID: nullableUUIDSchema,
    status: equipmentInstanceStatusSchema.optional(),
    qrCode: nullableStringSchema,
    serialNumber: nullableStringSchema,
});
export const updateEquipmentInstanceInputSchema = z
    .object({
    id: uuidSchema,
    identifier: trimmedNameSchema.optional(),
    notes: nullableStringSchema,
    acquisitionDate: nullableStringSchema,
    acquisitionPrice: nullableNumberSchema,
    vendorID: nullableUUIDSchema,
    status: equipmentInstanceStatusSchema.optional(),
    qrCode: nullableStringSchema,
    serialNumber: nullableStringSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment instance field must be updated");
export const deleteEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
});
export const createEquipmentImageInputSchema = z.object({
    id: uuidSchema,
    equipmentID: uuidSchema,
    imageURL: z.string().url(),
    displayOrder: z.number().int().min(0).nullable().optional(),
});
export const deleteEquipmentImageInputSchema = z.object({
    id: uuidSchema,
});
export const addEquipmentContentInputSchema = z
    .object({
    id: uuidSchema,
    parentEquipmentID: uuidSchema,
    /** Stock article; omit for a free line */
    childEquipmentID: uuidSchema.optional(),
    /** Name of a free line; omit for a stock article */
    name: z.string().trim().min(1).max(500).optional(),
    quantity: z.number().int().min(1).max(9999).optional(),
})
    .refine((args) => (args.childEquipmentID ? !args.name : !!args.name), {
    message: "Provide either childEquipmentID or name",
});
export const updateEquipmentContentNameInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(500),
});
export const updateEquipmentContentQuantityInputSchema = z.object({
    id: uuidSchema,
    quantity: z.number().int().min(1).max(9999),
});
export const removeEquipmentContentInputSchema = z.object({
    id: uuidSchema,
});
export const createEquipmentGroupInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    displayOrder: z.number().int().min(0).nullable().optional(),
});
export const updateEquipmentGroupInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    packingState: z.enum(["open", "packed"]).optional(),
    riggingState: z.enum(["open", "in_progress", "ready", "tested"]).optional(),
    displayOrder: z.number().int().min(0).nullable().optional(),
    isCollapsed: z.boolean().nullable().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one group field must be updated");
export const deleteEquipmentGroupInputSchema = z.object({
    id: uuidSchema,
});
export const deleteEquipmentAssignmentInputSchema = z.object({
    id: uuidSchema,
});
export const createTemporaryEquipmentInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    countryOfOrigin: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    weight: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
});
export const updateTemporaryEquipmentInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    countryOfOrigin: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    weight: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one temporary equipment field must be updated");
export const createEquipmentAssignmentInputSchema = z
    .object({
    ...assignmentEquipmentRefsShape,
    id: uuidSchema,
    groupID: uuidSchema,
    amount: z.number().int().min(1).max(9999).optional(),
    notes: z.string().max(5000).nullable().optional(),
    cameraPlanColumnKey: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .nullable()
        .optional(),
})
    .refine((args) => !(args.equipmentID && args.temporaryEquipmentID), "Use either equipmentID or temporaryEquipmentID, not both");
export const updateEquipmentAssignmentInputSchema = z
    .object({
    ...assignmentEquipmentRefsShape,
    id: uuidSchema,
    amount: z.number().int().min(1).max(9999).optional(),
    notes: z.string().max(5000).nullable().optional(),
    cameraPlanColumnKey: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .nullable()
        .optional(),
})
    .refine((args) => !(args.equipmentID && args.temporaryEquipmentID), "Use either equipmentID or temporaryEquipmentID, not both")
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one assignment field must be updated");
export const assignEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
    assignmentID: uuidSchema,
    instanceID: uuidSchema,
});
export const unassignEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
});
const cameraPlanMapTypeSchema = z.enum(["image", "embed", "mapbox"]);
const cameraPlanColumnTypeSchema = z.enum(["text", "select", "checkbox"]);
const cameraCustomValueSchema = z.union([
    z.string(),
    z.boolean(),
    z.array(z.string()),
]);
const cameraPlanOverlayTypeSchema = z.enum([
    "image",
    "rectangle",
    "ellipse",
    "line",
    "text",
]);
export function toZeroDate(value) {
    if (value === null)
        return null;
    if (value === undefined)
        return undefined;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day)
        return null;
    return Date.UTC(year, month - 1, day);
}
export const createCameraPlanInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    description: nullableStringSchema,
    mapType: cameraPlanMapTypeSchema.optional(),
    imageURL: nullableStringSchema,
    embedURL: nullableStringSchema,
    mapCenterLng: nullableNumberSchema,
    mapCenterLat: nullableNumberSchema,
    mapZoom: nullableNumberSchema,
    eventDate: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
});
export const updateCameraPlanInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    description: nullableStringSchema,
    mapType: cameraPlanMapTypeSchema.optional(),
    imageURL: nullableStringSchema,
    embedURL: nullableStringSchema,
    mapCenterLng: nullableNumberSchema,
    mapCenterLat: nullableNumberSchema,
    mapZoom: nullableNumberSchema,
    eventDate: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan field must be updated");
export const deleteCameraPlanInputSchema = z.object({
    id: uuidSchema,
});
export const createCameraPlanColumnInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    name: trimmedNameSchema,
    columnType: cameraPlanColumnTypeSchema,
    options: z.array(z.string()).optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const updateCameraPlanColumnInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    columnType: cameraPlanColumnTypeSchema.optional(),
    options: z.array(z.string()).optional(),
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan column field must be updated");
export const deleteCameraPlanColumnInputSchema = z.object({
    id: uuidSchema,
});
const cameraPlanOverlayFields = {
    overlayType: cameraPlanOverlayTypeSchema,
    positionX: z.number().optional(),
    positionY: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    rotation: z.number().optional(),
    color: z.string().min(1).max(100).optional(),
    opacity: z.number().optional(),
    strokeWidth: z.number().optional(),
    fill: z.boolean().optional(),
    imageURL: nullableStringSchema,
    label: nullableStringSchema,
    fontSize: z.number().optional(),
    points: z
        .array(z.object({ x: z.number(), y: z.number() }))
        .nullable()
        .optional(),
    zIndex: z.number().int().optional(),
    sortOrder: z.number().int().optional(),
    locked: z.boolean().optional(),
};
export const createCameraPlanOverlayInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    ...cameraPlanOverlayFields,
});
export const updateCameraPlanOverlayInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(cameraPlanOverlayFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan overlay field must be updated");
export const deleteCameraPlanOverlayInputSchema = z.object({
    id: uuidSchema,
});
export const createCameraInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    number: trimmedNameSchema,
    description: nullableStringSchema,
    icon: z.string().min(1).max(100).optional(),
    positionX: nullableNumberSchema,
    positionY: nullableNumberSchema,
    rotation: z.number().optional(),
    track: z.any().nullable().optional(),
    customValues: z.record(z.string(), cameraCustomValueSchema).optional(),
    sortOrder: z.number().int().min(0).optional(),
    color: z.string().min(1).max(100).optional(),
    equipmentGroupID: nullableUUIDSchema,
});
export const updateCameraInputSchema = z
    .object({
    id: uuidSchema,
    number: trimmedNameSchema.optional(),
    description: nullableStringSchema,
    icon: z.string().min(1).max(100).optional(),
    positionX: nullableNumberSchema,
    positionY: nullableNumberSchema,
    rotation: z.number().optional(),
    track: z.any().nullable().optional(),
    customValues: z.record(z.string(), cameraCustomValueSchema).optional(),
    sortOrder: z.number().int().min(0).optional(),
    color: z.string().min(1).max(100).optional(),
    equipmentGroupID: nullableUUIDSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera field must be updated");
export const deleteCameraInputSchema = z.object({
    id: uuidSchema,
});
export const createCameraFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
    cameraID: uuidSchema,
    functionAssignmentID: uuidSchema,
});
export const deleteCameraFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
});
function assertEquipmentWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit equipment plans");
    }
}
/** Enforces the tag hierarchy rules (max depth, no cycles). */
async function assertValidEquipmentTagParent(tx, tagId, parentId) {
    if (!parentId)
        return;
    const tags = await tx.run(equipmentPlanZql.equipmentTags);
    const error = validateEquipmentTagParent(tags ?? [], tagId, parentId);
    if (error)
        throw new Error(error);
}
async function replaceEquipmentTagAssignments(tx, equipmentID, tagIDs) {
    const existing = await tx.run(equipmentPlanZql.equipmentTagAssignments.where("equipmentID", equipmentID));
    for (const assignment of existing ?? []) {
        await tx.mutate.equipmentTagAssignments.delete({ id: assignment.id });
    }
    for (const tagID of tagIDs) {
        await tx.mutate.equipmentTagAssignments.insert({
            id: crypto.randomUUID(),
            equipmentID,
            tagID,
        });
    }
}
export const equipmentPlanMutatorDefinitions = {
    equipmentTags: {
        create: defineEquipmentMutator(createEquipmentTagInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await assertValidEquipmentTagParent(tx, null, args.parentID ?? null);
            await tx.mutate.equipmentTags.insert({
                id: args.id,
                name: args.name,
                parentID: args.parentID ?? null,
                icon: args.icon || null,
            });
        }),
        update: defineEquipmentMutator(updateEquipmentTagInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            if (args.parentID !== undefined) {
                await assertValidEquipmentTagParent(tx, args.id, args.parentID);
            }
            await tx.mutate.equipmentTags.update({
                id: args.id,
                ...(args.name !== undefined ? { name: args.name } : {}),
                ...(args.parentID !== undefined ? { parentID: args.parentID } : {}),
                ...(args.icon !== undefined ? { icon: args.icon || null } : {}),
            });
        }),
        delete: defineEquipmentMutator(equipmentTagIDInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentTags.delete({ id: args.id });
        }),
    },
    equipmentCatalog: {
        create: defineEquipmentMutator(createEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const { tagIDs = [], ...equipmentArgs } = args;
            await tx.mutate.equipment.insert({
                ...equipmentArgs,
                isSerialized: equipmentArgs.isSerialized ?? true,
                stockQuantity: equipmentArgs.isSerialized
                    ? 0
                    : (equipmentArgs.stockQuantity ?? 0),
            });
            await replaceEquipmentTagAssignments(tx, args.id, tagIDs);
        }),
        update: defineEquipmentMutator(updateEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const { tagIDs, ...equipmentArgs } = args;
            await tx.mutate.equipment.update({
                ...equipmentArgs,
                ...(equipmentArgs.isSerialized === true ? { stockQuantity: 0 } : {}),
            });
            if (tagIDs !== undefined) {
                await replaceEquipmentTagAssignments(tx, args.id, tagIDs);
            }
        }),
        softDelete: defineEquipmentMutator(softDeleteEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipment.update({
                id: args.id,
                deletedAt: Date.now(),
            });
        }),
    },
    equipmentInstances: {
        create: defineEquipmentMutator(createEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentInstances.insert({
                id: args.id,
                equipmentID: args.equipmentID,
                identifier: args.identifier,
                notes: args.notes ?? null,
                acquisitionDate: toZeroDate(args.acquisitionDate) ?? null,
                acquisitionPrice: args.acquisitionPrice ?? null,
                vendorID: args.vendorID ?? null,
                status: args.status ?? "active",
                qrCode: args.qrCode ?? null,
                serialNumber: args.serialNumber ?? null,
            });
        }),
        update: defineEquipmentMutator(updateEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentInstances.update({
                ...args,
                acquisitionDate: args.acquisitionDate !== undefined
                    ? (toZeroDate(args.acquisitionDate) ?? null)
                    : undefined,
            });
        }),
        delete: defineEquipmentMutator(deleteEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentInstances.delete({ id: args.id });
        }),
    },
    equipmentImages: {
        create: defineEquipmentMutator(createEquipmentImageInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentImages.insert({
                id: args.id,
                equipmentID: args.equipmentID,
                imageURL: args.imageURL,
                displayOrder: args.displayOrder ?? null,
            });
        }),
        delete: defineEquipmentMutator(deleteEquipmentImageInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentImages.delete({ id: args.id });
        }),
    },
    equipmentContents: {
        add: defineEquipmentMutator(addEquipmentContentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.insert({
                id: args.id,
                parentEquipmentID: args.parentEquipmentID,
                childEquipmentID: args.childEquipmentID ?? null,
                name: args.childEquipmentID ? null : (args.name ?? null),
                quantity: args.quantity ?? 1,
            });
        }),
        updateName: defineEquipmentMutator(updateEquipmentContentNameInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const content = await tx.run(equipmentPlanZql.equipmentContents.where("id", args.id).one());
            if (!content)
                throw new Error("Content not found");
            if (content.childEquipmentID) {
                throw new Error("Only free content lines can be renamed");
            }
            await tx.mutate.equipmentContents.update(args);
        }),
        updateQuantity: defineEquipmentMutator(updateEquipmentContentQuantityInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.update(args);
        }),
        remove: defineEquipmentMutator(removeEquipmentContentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.delete({ id: args.id });
        }),
    },
    equipmentPlan: {
        createGroup: defineEquipmentMutator(createEquipmentGroupInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentGroups.insert({
                id: args.id,
                projectID: args.projectID,
                name: args.name,
                packingState: "open",
                riggingState: "open",
                displayOrder: args.displayOrder ?? 0,
                isCollapsed: false,
            });
        }),
        updateGroup: defineEquipmentMutator(updateEquipmentGroupInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentGroups.update(args);
        }),
        deleteGroup: defineEquipmentMutator(deleteEquipmentGroupInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentGroups.delete({ id: args.id });
        }),
        createTemporaryEquipment: defineEquipmentMutator(createTemporaryEquipmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.temporaryEquipment.insert(args);
        }),
        updateTemporaryEquipment: defineEquipmentMutator(updateTemporaryEquipmentInputSchema, async ({ tx, ctx, args }) => {
            assertEquipmentWriteAccess(ctx);
            const { id, ...updates } = args;
            if (!id)
                throw new Error("Temporary equipment id is required");
            await tx.mutate.temporaryEquipment.update({
                ...updates,
                id,
            });
        }),
        createAssignment: defineEquipmentMutator(createEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentAssignments.insert({
                id: args.id,
                groupID: args.groupID,
                equipmentID: args.equipmentID ?? null,
                temporaryEquipmentID: args.temporaryEquipmentID ?? null,
                amount: args.amount ?? 1,
                notes: args.notes ?? null,
                cameraPlanColumnKey: args.cameraPlanColumnKey ?? null,
            });
        }),
        updateAssignment: defineEquipmentMutator(updateEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentAssignments.update(args);
        }),
        deleteAssignment: defineEquipmentMutator(deleteEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentAssignments.delete({ id: args.id });
        }),
        assignInstance: defineEquipmentMutator(assignEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentInstanceAssignments.insert(args);
        }),
        unassignInstance: defineEquipmentMutator(unassignEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.projectEquipmentInstanceAssignments.delete({
                id: args.id,
            });
        }),
    },
    cameraPlans: {
        createPlan: defineEquipmentMutator(createCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlans.insert({
                id: args.id,
                projectID: args.projectID,
                name: args.name,
                description: args.description ?? null,
                mapType: args.mapType ?? "image",
                imageURL: args.imageURL ?? null,
                embedURL: args.embedURL ?? null,
                mapCenterLng: args.mapCenterLng ?? null,
                mapCenterLat: args.mapCenterLat ?? null,
                mapZoom: args.mapZoom ?? 14,
                eventDate: toZeroDate(args.eventDate) ?? null,
                sortOrder: args.sortOrder ?? 0,
            });
        }),
        updatePlan: defineEquipmentMutator(updateCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlans.update({
                ...args,
                eventDate: toZeroDate(args.eventDate),
            });
        }),
        deletePlan: defineEquipmentMutator(deleteCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlans.delete({ id: args.id });
        }),
        createColumn: defineEquipmentMutator(createCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanColumns.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                name: args.name,
                columnType: args.columnType,
                options: args.options ?? [],
                sortOrder: args.sortOrder ?? 0,
            });
        }),
        updateColumn: defineEquipmentMutator(updateCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanColumns.update(args);
        }),
        deleteColumn: defineEquipmentMutator(deleteCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanColumns.delete({ id: args.id });
        }),
        createOverlay: defineEquipmentMutator(createCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanOverlays.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                overlayType: args.overlayType,
                positionX: args.positionX ?? 0.5,
                positionY: args.positionY ?? 0.5,
                width: args.width ?? 0.1,
                height: args.height ?? 0.1,
                rotation: args.rotation ?? 0,
                color: args.color ?? "#6b7280",
                opacity: args.opacity ?? 1,
                strokeWidth: args.strokeWidth ?? 2,
                fill: args.fill ?? false,
                imageURL: args.imageURL ?? null,
                label: args.label ?? null,
                fontSize: args.fontSize ?? 14,
                points: args.points ?? null,
                zIndex: args.zIndex ?? 0,
                sortOrder: args.sortOrder ?? 0,
                locked: args.locked ?? false,
            });
        }),
        updateOverlay: defineEquipmentMutator(updateCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanOverlays.update(args);
        }),
        deleteOverlay: defineEquipmentMutator(deleteCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanOverlays.delete({ id: args.id });
        }),
        createCamera: defineEquipmentMutator(createCameraInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameras.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                number: args.number,
                description: args.description ?? null,
                icon: args.icon ?? "tripod",
                positionX: args.positionX ?? null,
                positionY: args.positionY ?? null,
                rotation: args.rotation ?? 0,
                track: args.track ?? null,
                customValues: args.customValues ?? {},
                sortOrder: args.sortOrder ?? 0,
                color: args.color ?? "#3b82f6",
                equipmentGroupID: args.equipmentGroupID ?? null,
            });
        }),
        updateCamera: defineEquipmentMutator(updateCameraInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameras.update(args);
        }),
        deleteCamera: defineEquipmentMutator(deleteCameraInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameras.delete({ id: args.id });
        }),
        addOperator: defineEquipmentMutator(createCameraFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraFunctionAssignments.insert(args);
        }),
        removeOperator: defineEquipmentMutator(deleteCameraFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraFunctionAssignments.delete({ id: args.id });
        }),
    },
};
export const equipmentPlanMutators = defineMutators(equipmentPlanMutatorDefinitions);
